import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EyeOff, MessageSquare, Plus } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { SupportTask, TaskStatus } from '../../../common/models/support.model';
import { TASK_STATUSES } from '../../../common/models/support.model';
import { TASK_PRIORITY_META, TASK_STATUS_META, TASK_TYPE_META } from '../support-meta';
import { PriorityBars } from '../components/TaskBadge';
import { StaffAvatar } from './StaffPeople';

export type Columns = Record<TaskStatus, SupportTask[]>;

export function toColumns(tasks: SupportTask[]): Columns {
  const cols = Object.fromEntries(TASK_STATUSES.map((s) => [s, [] as SupportTask[]])) as Columns;
  for (const t of tasks) (cols[t.status] || cols.todo).push(t);
  for (const s of TASK_STATUSES) cols[s].sort((a, b) => a.order - b.order);
  return cols;
}

const columnOf = (cols: Columns, id: string): TaskStatus | undefined =>
  TASK_STATUSES.find((s) => cols[s].some((t) => t._id === id));

const targetColumn = (cols: Columns, overId: string): TaskStatus | undefined =>
  overId.startsWith('col:') ? (overId.slice(4) as TaskStatus) : columnOf(cols, overId);

/** While dragging: a card crossing into another column lands at the hovered card, or at the end. */
export function moveAcross(cols: Columns, id: string, overId: string): Columns {
  const from = columnOf(cols, id);
  const to = targetColumn(cols, overId);
  if (!from || !to || from === to) return cols;
  const card = cols[from].find((t) => t._id === id)!;
  const overIndex = cols[to].findIndex((t) => t._id === overId);
  const insertAt = overIndex >= 0 ? overIndex : cols[to].length;
  const next = { ...cols, [from]: cols[from].filter((t) => t._id !== id), [to]: [...cols[to]] };
  next[to].splice(insertAt, 0, { ...card, status: to });
  return next;
}

/**
 * On drop: the card's column and its new neighbours, or null when nothing moved.
 * `cols` is the board as dragged (after moveAcross); `tasks` the board before.
 */
export function finishDrop(
  cols: Columns,
  tasks: SupportTask[],
  id: string,
  overId: string,
): { status: TaskStatus; beforeId?: string; afterId?: string } | null {
  const status = columnOf(cols, id);
  if (!status) return null;
  const list = [...cols[status]];
  const original = tasks.find((t) => t._id === id);
  // A card that changed column already sits where moveAcross put it; only a
  // drop within its own column reorders here (or it would move twice).
  const from = list.findIndex((t) => t._id === id);
  const overIndex = list.findIndex((t) => t._id === overId);
  if (original?.status === status && overIndex >= 0 && overIndex !== from) {
    const [card] = list.splice(from, 1);
    list.splice(overIndex, 0, card);
  }
  const at = list.findIndex((t) => t._id === id);
  const originalList = toColumns(tasks)[original?.status || status];
  const unchanged = original?.status === status && originalList.map((t) => t._id).join() === list.map((t) => t._id).join();
  if (unchanged) return null;
  return { status, beforeId: list[at - 1]?._id, afterId: list[at + 1]?._id };
}

/** Card content, shared by the list and the drag overlay. */
export function TaskCardBody({ task }: { task: SupportTask }) {
  const { t } = useTranslation();
  const type = TASK_TYPE_META[task.type];
  const TypeIcon = type.icon;
  const priority = TASK_PRIORITY_META[task.priority];
  return (
    <>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <TypeIcon className={cn('size-3.5', type.tone)} />
        <span className="tabular-nums">#{task.number}</span>
        {!task.visibleToVendor && <EyeOff className="size-3" aria-label={t('SUPPORT.BOARD.PRIVATE')} />}
        <PriorityBars bars={priority.bars} tone={priority.tone} className="ms-auto" />
      </div>
      <p className="mt-1.5 line-clamp-2 text-sm leading-snug">{task.title}</p>
      {task.labels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {task.labels.slice(0, 3).map((l) => (
            <span key={l} className="rounded-md border px-1.5 py-0.5 text-[10px] text-muted-foreground">{l}</span>
          ))}
        </div>
      )}
      <div className="mt-2.5 flex items-center gap-2 text-[11px] text-muted-foreground">
        {task.vendorName && <span className="min-w-0 truncate">{task.vendorName}</span>}
        {task.conversations.length > 0 && (
          <span className="flex shrink-0 items-center gap-0.5" title={t('SUPPORT.BOARD.CONVERSATIONS', { count: task.conversations.length })}>
            <MessageSquare className="size-3" />
            {task.conversations.length}
          </span>
        )}
        <StaffAvatar email={task.assignee} className="ms-auto" />
      </div>
    </>
  );
}

function SortableCard({ task, onOpen }: { task: SupportTask; onOpen: (t: SupportTask) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task._id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      onClick={() => onOpen(task)}
      className={cn(
        'cursor-grab select-none rounded-xl border bg-card p-3 transition-colors hover:bg-muted/40 active:cursor-grabbing',
        isDragging && 'opacity-30',
      )}
      data-testid="task-card"
    >
      <TaskCardBody task={task} />
    </div>
  );
}

function Column({ status, tasks, onOpen, onAdd }: { status: TaskStatus; tasks: SupportTask[]; onOpen: (t: SupportTask) => void; onAdd: (s: TaskStatus) => void }) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: `col:${status}` });
  const meta = TASK_STATUS_META[status];
  return (
    <div
      ref={setNodeRef}
      className={cn('flex min-h-0 w-72 shrink-0 flex-col rounded-2xl border bg-muted/40 p-2 transition-colors', isOver && 'border-foreground/40 bg-muted')}
      data-testid={`board-column-${status}`}
    >
      <div className="flex items-center gap-2 px-1.5 pb-2 pt-1">
        <span className={cn('size-2 rounded-full', meta.dot)} />
        <span className="text-sm font-medium">{t(meta.labelKey)}</span>
        <span className="text-sm tabular-nums text-muted-foreground">{tasks.length}</span>
        <button
          type="button"
          onClick={() => onAdd(status)}
          className="ms-auto rounded-md p-1 text-muted-foreground hover:bg-background hover:text-foreground"
          aria-label={t('SUPPORT.BOARD.ADD_IN', { column: t(meta.labelKey) })}
        >
          <Plus className="size-3.5" />
        </button>
      </div>
      <SortableContext items={tasks.map((x) => x._id)} strategy={verticalListSortingStrategy}>
        <div className="flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto p-0.5">
          {tasks.map((task) => <SortableCard key={task._id} task={task} onOpen={onOpen} />)}
          {tasks.length === 0 && <p className="py-6 text-center text-xs text-muted-foreground">{t('SUPPORT.BOARD.EMPTY_COLUMN')}</p>}
        </div>
      </SortableContext>
    </div>
  );
}

/**
 * Drag a card to another column or another place in its own column. The move
 * shows at once; the server gets the new column and the cards now above and
 * below it, and the board reloads if the move is refused.
 */
export function TaskBoard({
  tasks,
  onMove,
  onOpen,
  onAdd,
}: {
  tasks: SupportTask[];
  onMove: (id: string, move: { status: TaskStatus; beforeId?: string; afterId?: string }) => void;
  onOpen: (t: SupportTask) => void;
  onAdd: (status: TaskStatus) => void;
}) {
  const [dragCols, setDragCols] = useState<Columns | null>(null);
  const [active, setActive] = useState<SupportTask | null>(null);
  const columns = useMemo(() => dragCols ?? toColumns(tasks), [dragCols, tasks]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragStart = (e: DragStartEvent) => {
    setActive(tasks.find((t) => t._id === e.active.id) || null);
    setDragCols(toColumns(tasks));
  };

  // Crossing into another column: move the card there as it's dragged.
  const onDragOver = (e: DragOverEvent) => {
    const overId = e.over?.id ? String(e.over.id) : null;
    if (!overId) return;
    setDragCols((cols) => (cols ? moveAcross(cols, String(e.active.id), overId) : cols));
  };

  const onDragEnd = (e: DragEndEvent) => {
    const id = String(e.active.id);
    const overId = e.over?.id ? String(e.over.id) : null;
    const cols = dragCols;
    setActive(null);
    setDragCols(null);
    if (!cols || !overId) return;
    const move = finishDrop(cols, tasks, id, overId);
    if (move) onMove(id, move);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => { setActive(null); setDragCols(null); }}
    >
      <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2" data-testid="task-board">
        {TASK_STATUSES.map((s) => (
          <Column key={s} status={s} tasks={columns[s]} onOpen={onOpen} onAdd={onAdd} />
        ))}
      </div>
      <DragOverlay>
        {active ? (
          <div className="w-72 rotate-1 cursor-grabbing rounded-xl border bg-card p-3 shadow-lg">
            <TaskCardBody task={active} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
